import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { departmentService, doctorService } from '../services/dataService';
import { appointmentService } from '../services/appointmentService';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import { FaCalendarAlt, FaClock, FaUserMd, FaHospital } from 'react-icons/fa';
import './BookAppointment.css';

const BookAppointment = () => {
  const [bookingMode, setBookingMode] = useState('doctor'); // 'doctor' or 'disease'
  const [departments, setDepartments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadDepartments();
  }, []);

  const loadDepartments = async () => {
    try {
      const response = await departmentService.getAllDepartments();
      setDepartments(response.data.map(dept => ({ value: dept.id, label: dept.name })));
    } catch (error) {
      console.error('Error loading departments:', error);
    }
  };

  const loadDoctorsByDepartment = async (departmentId) => {
    try {
      const response = await appointmentService.getDoctorsByDepartment(departmentId);
      setDoctors(response.data.map(doc => ({
        value: doc.id,
        label: `Dr. ${doc.name} - ${doc.specialization}`,
        fee: doc.consultation_fee
      })));
    } catch (error) {
      toast.error('Failed to load doctors');
    }
  };

  const loadAvailableSlots = async (doctorId, date) => {
    if (!doctorId || !date) return;
    
    try {
      setLoading(true);
      const dateStr = date.toISOString().split('T')[0];
      const response = await appointmentService.getAvailableSlots(doctorId, dateStr);
      setAvailableSlots(response.data);
    } catch (error) {
      toast.error('Failed to load available slots');
      setAvailableSlots([]);
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      booking_mode: 'doctor',
      department_id: '',
      doctor_id: '',
      disease_description: '',
      appointment_date: '',
      time_slot: '',
      symptoms: '',
      notes: '',
    },
    validationSchema: Yup.object({
      department_id: Yup.string().required('Department is required'),
      doctor_id: bookingMode === 'doctor' ? Yup.string().required('Doctor is required') : Yup.string(),
      disease_description: bookingMode === 'disease' ? Yup.string().required('Disease description is required') : Yup.string(),
      appointment_date: Yup.date().required('Appointment date is required'),
      time_slot: Yup.string().required('Time slot is required'),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        await appointmentService.bookAppointment(values);
        toast.success('Appointment booked successfully! Confirmation email sent.');
        navigate('/my-appointments');
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to book appointment');
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (formik.values.department_id) {
      loadDoctorsByDepartment(formik.values.department_id);
    }
  }, [formik.values.department_id]);

  useEffect(() => {
    if (formik.values.doctor_id && selectedDate) {
      loadAvailableSlots(formik.values.doctor_id, selectedDate);
    }
  }, [formik.values.doctor_id, selectedDate]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    formik.setFieldValue('appointment_date', date ? date.toISOString().split('T')[0] : '');
  };

  return (
    <div className="book-appointment-page">
      <div className="page-header">
        <div className="container">
          <h1>Book Appointment</h1>
          <p>Schedule your consultation with our expert doctors</p>
        </div>
      </div>

      <div className="container">
        <div className="appointment-form-wrapper">
          {/* Booking Mode Selection */}
          <div className="booking-mode-selector">
            <button
              type="button"
              className={`mode-btn ${bookingMode === 'doctor' ? 'active' : ''}`}
              onClick={() => {
                setBookingMode('doctor');
                formik.setFieldValue('booking_mode', 'doctor');
              }}
            >
              <FaUserMd /> Book by Doctor
            </button>
            <button
              type="button"
              className={`mode-btn ${bookingMode === 'disease' ? 'active' : ''}`}
              onClick={() => {
                setBookingMode('disease');
                formik.setFieldValue('booking_mode', 'disease');
              }}
            >
              <FaHospital /> Book by Disease/Department
            </button>
          </div>

          <form onSubmit={formik.handleSubmit} className="appointment-form">
            {/* Department Selection */}
            <div className="form-section">
              <h3>Select Department</h3>
              <div className="form-group">
                <label>Department *</label>
                <Select
                  options={departments}
                  onChange={(option) => formik.setFieldValue('department_id', option?.value || '')}
                  placeholder="Choose a department..."
                  isClearable
                />
                {formik.touched.department_id && formik.errors.department_id && (
                  <div className="invalid-feedback">{formik.errors.department_id}</div>
                )}
              </div>
            </div>

            {/* Doctor or Disease Selection */}
            {bookingMode === 'doctor' ? (
              <div className="form-section">
                <h3>Select Doctor</h3>
                <div className="form-group">
                  <label>Doctor *</label>
                  <Select
                    options={doctors}
                    onChange={(option) => {
                      formik.setFieldValue('doctor_id', option?.value || '');
                      setSelectedDoctor(option);
                    }}
                    placeholder="Choose a doctor..."
                    isDisabled={!formik.values.department_id}
                    isClearable
                  />
                  {formik.touched.doctor_id && formik.errors.doctor_id && (
                    <div className="invalid-feedback">{formik.errors.doctor_id}</div>
                  )}
                  {selectedDoctor && (
                    <div className="fee-info">
                      Consultation Fee: ₹{selectedDoctor.fee}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="form-section">
                <h3>Describe Your Condition</h3>
                <div className="form-group">
                  <label>Disease/Condition Description *</label>
                  <textarea
                    name="disease_description"
                    rows="4"
                    className={`form-control ${formik.touched.disease_description && formik.errors.disease_description ? 'is-invalid' : ''}`}
                    placeholder="Describe your symptoms or condition..."
                    {...formik.getFieldProps('disease_description')}
                  />
                  {formik.touched.disease_description && formik.errors.disease_description && (
                    <div className="invalid-feedback">{formik.errors.disease_description}</div>
                  )}
                  <small className="form-text">Our system will recommend the best doctor for your condition</small>
                </div>
              </div>
            )}

            {/* Date and Time Selection */}
            <div className="form-section">
              <h3>Select Date & Time</h3>
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label><FaCalendarAlt /> Appointment Date *</label>
                    <DatePicker
                      selected={selectedDate}
                      onChange={handleDateChange}
                      minDate={new Date()}
                      dateFormat="yyyy-MM-dd"
                      className="form-control"
                      placeholderText="Select date..."
                      disabled={bookingMode === 'doctor' && !formik.values.doctor_id}
                    />
                    {formik.touched.appointment_date && formik.errors.appointment_date && (
                      <div className="invalid-feedback">{formik.errors.appointment_date}</div>
                    )}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <label><FaClock /> Time Slot *</label>
                    <select
                      name="time_slot"
                      className={`form-control ${formik.touched.time_slot && formik.errors.time_slot ? 'is-invalid' : ''}`}
                      disabled={!selectedDate || (bookingMode === 'doctor' && !formik.values.doctor_id)}
                      {...formik.getFieldProps('time_slot')}
                    >
                      <option value="">Select time slot...</option>
                      {availableSlots.map((slot, index) => (
                        <option key={index} value={slot.time}>
                          {slot.time} {slot.available ? '' : '(Booked)'}
                        </option>
                      ))}
                    </select>
                    {formik.touched.time_slot && formik.errors.time_slot && (
                      <div className="invalid-feedback">{formik.errors.time_slot}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div className="form-section">
              <h3>Additional Information</h3>
              <div className="form-group">
                <label>Symptoms</label>
                <textarea
                  name="symptoms"
                  rows="3"
                  className="form-control"
                  placeholder="List your symptoms..."
                  {...formik.getFieldProps('symptoms')}
                />
              </div>

              <div className="form-group">
                <label>Additional Notes</label>
                <textarea
                  name="notes"
                  rows="3"
                  className="form-control"
                  placeholder="Any additional information for the doctor..."
                  {...formik.getFieldProps('notes')}
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Booking...' : 'Confirm Appointment'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BookAppointment;
